import { useTranslation } from "react-i18next";
import type { GitRepo } from "../../types/cloud";

interface RepoCardProps {
  repo: GitRepo;
  onImport: (repo: GitRepo) => void;
  importing?: boolean;
}

const LANGUAGE_COLORS: Record<string, string> = {
  TypeScript: "#3178c6",
  JavaScript: "#f1e05a",
  Python: "#3572A5",
  Go: "#00ADD8",
  Rust: "#dea584",
  Java: "#b07219",
  Ruby: "#701516",
  C: "#555555",
  "C++": "#f34b7d",
  "C#": "#178600",
  PHP: "#4F5D95",
  Swift: "#F05138",
  Kotlin: "#A97BFF",
  Shell: "#89e051",
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function RepoCard({ repo, onImport, importing }: RepoCardProps) {
  const { t } = useTranslation();
  const langColor = repo.language ? (LANGUAGE_COLORS[repo.language] ?? "#6b7280") : null;

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 hover:border-[#444] transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#f5f5f5] font-semibold text-sm truncate">{repo.name}</span>
            <span className="text-[#666] text-xs flex-shrink-0">
              {repo.owner}/{repo.name}
            </span>
          </div>
          {repo.description && (
            <p className="text-[#999] text-xs mb-2 truncate">{repo.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-[#666]">
            {repo.language && langColor && (
              <span className="flex items-center gap-1">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: langColor }}
                />
                {repo.language}
              </span>
            )}
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                repo.isPrivate
                  ? "bg-yellow-500/20 text-yellow-400"
                  : "bg-green-500/20 text-green-400"
              }`}
            >
              {repo.isPrivate ? t('dashboard.repo.private') : t('dashboard.repo.public')}
            </span>
            <span>{t('dashboard.repo.updated', { time: formatRelativeTime(repo.updatedAt) })}</span>
          </div>
        </div>
        <button
          onClick={() => onImport(repo)}
          disabled={importing}
          className="flex-shrink-0 px-4 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {importing ? "..." : t('dashboard.repo.import')}
        </button>
      </div>
    </div>
  );
}
